import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

export type DataMode = 'demo' | 'real'

type ModeChangeMeta = {
  mode: DataMode
  previousMode: DataMode
  changedAt: number
}

type DataModeContextValue = {
  mode: DataMode
  setMode: (mode: DataMode) => void
  isDemo: boolean
  isReal: boolean
  lastChange: ModeChangeMeta | null
}

const STORAGE_KEY = 'sensei:data-mode'

const DataModeContext = createContext<DataModeContextValue | undefined>(undefined)

const readDefaultMode = (): DataMode => {
  if (typeof window === 'undefined') {
    return 'real'
  }

  // Safari privacy mode veya bazı edge case'lerde localStorage null olabilir
  if (!window.localStorage) {
    return 'real'
  }

  let stored: string | null = null
  try {
    stored = window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // localStorage erişim hatası (SecurityError vb.)
    return 'real'
  }
  if (stored === 'real' || stored === 'demo') {
    return stored
  }

  return 'real'
}

export function DataModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<DataMode>(() => readDefaultMode())
  const [lastChange, setLastChange] = useState<ModeChangeMeta | null>(null)

  const setMode = useCallback((nextMode: DataMode) => {
    setModeState((current) => {
      if (current === nextMode) {
        return current
      }

      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(STORAGE_KEY, nextMode)
        } catch (error) {
          console.warn('Veri modu kaydedilemedi.', error)
        }
      }

      setLastChange({ mode: nextMode, previousMode: current, changedAt: Date.now() })
      return nextMode
    })
  }, [])

  const value = useMemo<DataModeContextValue>(
    () => ({
      mode,
      setMode,
      isDemo: mode === 'demo',
      isReal: mode === 'real',
      lastChange,
    }),
    [lastChange, mode, setMode],
  )

  return <DataModeContext.Provider value={value}>{children}</DataModeContext.Provider>
}

export function useDataMode(): DataModeContextValue {
  const context = useContext(DataModeContext)

  if (!context) {
    throw new Error('useDataMode yalnızca DataModeProvider içinde kullanılabilir.')
  }

  return context
}
